import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import CinematicBackground from "@/components/CinematicBackground";
import { useLang } from "@/i18n/LanguageContext";
import { useApp } from "@/context/AppContext";

const SECTIONS = [
  { to: "/app/cinevision", tKey: "sec_cinevision", icon: "fa-clapperboard", color: "#f2c66b", tint: "rgba(242,198,107,0.16)" },
  { to: "/app/analysis", tKey: "sec_analysis", icon: "fa-brain", color: "#4ea8ff", tint: "rgba(78,168,255,0.14)" },
  { to: "/app/camera", tKey: "sec_camera", icon: "fa-vr-cardboard", color: "#d9b46a", tint: "rgba(217,180,106,0.14)" },
  { to: "/app/templates", tKey: "sec_templates", icon: "fa-clapperboard", color: "#e78fa3", tint: "rgba(231,143,163,0.14)" },
  { to: "/app/filters", tKey: "sec_filters", icon: "fa-wand-magic-sparkles", color: "#7bd88f", tint: "rgba(123,216,143,0.14)" },
  { to: "/app/training", tKey: "sec_training", icon: "fa-dumbbell", color: "#c46bff", tint: "rgba(196,107,255,0.14)" },
];

export default function MainScreen() {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { presentation, togglePresentation } = useApp();
  const rtl = lang === "ar";
  const chevron = rtl ? "fa-chevron-left" : "fa-chevron-right";

  return (
    <div className="relative px-4 pt-8" dir={rtl ? "rtl" : "ltr"} data-testid="main-screen">
      <CinematicBackground />

      {/* دخول متزامن: حلقة عدسة تتفتّح عند فتح الرئيسية */}
      <div className="iris-open-ring" />

      <motion.div initial={{ scale: 1.05 }} animate={{ scale: 1 }} transition={{ duration: 0.6, ease: "easeOut" }}>
        <header className="mb-6 flex items-center justify-between">
          <div>
            <p className="font-display tracking-[0.25em] text-ai text-xs mb-1">CINEARt AI</p>
            <h1 className="font-display text-2xl gold-gradient-text">{t("appSubtitle")}</h1>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={togglePresentation}
              data-testid="toggle-presentation"
              className="h-12 px-4 rounded-full glass flex items-center gap-2 text-gold hover:brightness-125 transition"
              style={{ border: "1px solid rgba(217,180,106,0.4)" }}
              title={t("presentationHint")}
            >
              <i className={`fa-solid ${presentation ? "fa-compress" : "fa-tv"} text-base`} />
              <span className="text-xs font-display hidden xs:inline">{presentation ? t("exitPresentation") : t("presentationMode")}</span>
            </button>
            <button
              onClick={() => navigate("/app/settings")}
              data-testid="open-settings"
              className="w-12 h-12 rounded-full glass glass-blue flex items-center justify-center text-gold hover:brightness-125 transition"
              title={t("settings")}
            >
              <i className="fa-solid fa-gear text-xl" />
            </button>
          </div>
        </header>

        <div className="space-y-4" data-testid="sections-grid">
          {SECTIONS.map((s, i) => (
            <motion.button
              key={s.to}
              data-testid={`section-${i + 1}`}
              initial={{ y: 20 }}
              animate={{ y: 0 }}
              transition={{ delay: i * 0.06 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate(s.to)}
              className="w-full glass rounded-3xl p-5 flex items-center gap-4 text-start relative overflow-hidden"
              style={{ borderColor: `${s.color}44` }}
            >
              <div className="absolute -left-6 -bottom-6 w-24 h-24 rounded-full" style={{ background: `radial-gradient(circle, ${s.tint}, transparent 70%)` }} />
              <div className="w-14 h-14 rounded-2xl flex items-center justify-center shrink-0" style={{ background: s.tint, border: `1px solid ${s.color}55` }}>
                <i className={`fa-solid ${s.icon} text-2xl`} style={{ color: s.color }} />
              </div>
              <div className="flex-1">
                <h3 className="font-display text-lg text-slate-100 mb-0.5">{t(`${s.tKey}_t`)}</h3>
                <p className="text-xs text-slate-400">{t(`${s.tKey}_d`)}</p>
              </div>
              <i className={`fa-solid ${chevron} text-slate-500`} />
            </motion.button>
          ))}
        </div>
      </motion.div>
    </div>
  );
}
